// src/schedule/MobileFilterFab.jsx
import React, { useMemo, useEffect, useState } from "react";
import { MdFilterList, MdChevronLeft, MdChevronRight } from "react-icons/md";
import EmployeeSearchSelect from "./EmployeeSearchSelect";
import ThemeToggle from "../theme/ThemeToggle";
import FabPortal from "./FabPortal";

// Stały adres Twojego kalendarza (prywatny embed – dokładnie ten, który podałeś)
const GCAL_EMBED_SRC =
    "https://calendar.google.com/calendar/embed?src=712ijfdfion8cfa72o0sdberl8%40group.calendar.google.com&ctz=Europe%2FWarsaw";

// ── helpers do poprawnego operowania na kluczu "YYYY-MM" (1..12, zero-padded) ──
const parseMonthKey = (key) => {
    const [ys, ms] = String(key || "").split("-");
    let y = Number(ys || 0);
    let m1 = Number(ms || 1); // 1..12
    if (!Number.isFinite(y)) y = new Date().getFullYear();
    if (!Number.isFinite(m1) || m1 < 1 || m1 > 12) m1 = 1;
    return { y, m1 };
};

const formatMonthKey = (y, m1) =>
    `${String(y)}-${String(m1).padStart(2, "0")}`;

export default function MobileFilterFab({
    open,
    onToggle,
    activeMonthKey,
    setActiveMonthKey,
    months,
    employees,
    selectedEmployee,
    setSelectedEmployee,
    currentView,          // "main" | "theatre" | "all"
    setCurrentView,
    tbdOnly,
    setTbdOnly,
    setShowGoogleCalendar,
    showGoogleCalendar,
    gcalSize, // { width, height, max } – opcjonalne
    eventSearchQuery = "",
    onChangeEventSearchQuery = () => { },
}) {
    // Używamy 1-based m1; label bierze months[m1-1]
    const { y, m1, label } = useMemo(() => {
        const { y, m1 } = parseMonthKey(activeMonthKey);
        const monthName = (months && months[m1 - 1]) || `Miesiąc ${m1}`;
        return { y, m1, label: `${monthName} ${y}` };
    }, [activeMonthKey, months]);

    // Zmiana miesiąca: wynik zawsze "YYYY-MM" (1..12, zero-padded)
    const changeMonth = (delta) => {
        let ny = y;
        let nm1 = m1 + delta; // 1..12
        if (nm1 < 1) {
            nm1 = 12;
            ny = y - 1;
        } else if (nm1 > 12) {
            nm1 = 1;
            ny = y + 1;
        }
        setActiveMonthKey(formatMonthKey(ny, nm1));
    };

    // ——— Embed Google Calendar (prywatny, bez publicznego ID) ———
    const lang = (navigator.language || "pl-PL").split("-")[0] || "pl";
    const enhancedSrc =
        GCAL_EMBED_SRC +
        `&mode=MONTH&showTitle=0&showTabs=0&showCalendars=0&showPrint=0&wkst=2&hl=${encodeURIComponent(
            lang
        )}`;

    const iframeDarkStyle = {
        border: 0,
        width: "100%",
        height: "100%",
        filter: "invert(0.9) hue-rotate(180deg) brightness(0.9) contrast(0.95)",
    };

    const sideW = gcalSize?.width ?? 520;
    const sideH = gcalSize?.height ?? 420;

    const [frameLoaded, setFrameLoaded] = useState(false);
    const [showFallback, setShowFallback] = useState(false);
    useEffect(() => {
        setFrameLoaded(false);
        setShowFallback(false);
        if (!open || !showGoogleCalendar) return;
        const t = setTimeout(() => {
            if (!frameLoaded) setShowFallback(true);
        }, 2000);
        return () => clearTimeout(t);
    }, [open, showGoogleCalendar, frameLoaded]);

    const handleToggleGCal = (e) => {
        e?.preventDefault?.();
        e?.stopPropagation?.();
        setShowGoogleCalendar((v) => !v);
    };

    return (
        <>
            {/* FAB – używamy Twojej klasy 'mobile-fab' */}
            <button
                onClick={onToggle}
                aria-label="Filtry"
                className="mobile-fab"
                title="Filtry i ustawienia"
            >
                <MdFilterList size={28} color="#60A5FA" />
            </button>

            <FabPortal open={open} onClose={onToggle}>
                {open && (
                    <>
                        <div className="fab-backdrop" onClick={onToggle} />

                        <div className="mobile-fab-panel" role="dialog" aria-modal="true">
                            {/* Toggle podglądu */}
                            <div className="panel-section">
                                <button
                                    onClick={handleToggleGCal}
                                    className="w-full text-left px-3 py-2 rounded bg-gray-800 border border-gray-700 hover:bg-gray-700"
                                    title="Pokaż/ukryj mini-podgląd kalendarza"
                                >
                                    {showGoogleCalendar
                                        ? "📅 Schowaj podgląd kalendarza"
                                        : "📅 Pokaż podgląd kalendarza"}
                                </button>
                                {gcalSize?.max > 0 && (
                                    <div className="mt-1 text-xs text-gray-400">
                                        {`Najwięcej wydarzeń jednego dnia: ${gcalSize.max}`}
                                    </div>
                                )}
                            </div>

                            {/* Mini-podgląd inline (mobile) */}
                            {showGoogleCalendar && (
                                <div
                                    className="md:hidden mb-3 -mx-1 rounded-lg border border-gray-700 overflow-hidden relative"
                                    style={{ height: sideH }}
                                >
                                    <iframe
                                        title="Google Calendar Preview (inline)"
                                        src={enhancedSrc}
                                        frameBorder="0"
                                        scrolling="no"
                                        loading="eager"
                                        style={iframeDarkStyle}
                                        onLoad={() => setFrameLoaded(true)}
                                    />
                                    {showFallback && !frameLoaded && (
                                        <div className="absolute inset-0 bg-gray-900/85 text-gray-100 flex flex-col items-center justify-center px-3 text-sm">
                                            <div className="mb-2 text-center">
                                                Podgląd prywatnego kalendarza został zablokowany przez przeglądarkę.
                                            </div>
                                            <a
                                                className="underline"
                                                href={enhancedSrc}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                Otwórz w nowej karcie
                                            </a>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Widok */}
                            <div className="panel-section">
                                <div className="panel-label">Widok</div>
                                <div className="grid grid-cols-2 gap-2">
                                    <button
                                        onClick={() => setCurrentView("main")}
                                        className={`px-3 py-2 rounded-lg border ${currentView === "main"
                                                ? "bg-blue-600 border-blue-500"
                                                : "bg-gray-700 border-gray-600 hover:bg-gray-650"
                                            }`}
                                    >
                                        📅 Main
                                    </button>
                                    <button
                                        onClick={() => setCurrentView("theatre")}
                                        className={`px-3 py-2 rounded-lg border ${currentView === "theatre"
                                                ? "bg-blue-600 border-blue-500"
                                                : "bg-gray-700 border-gray-600 hover:bg-gray-650"
                                            }`}
                                    >
                                        🎭 Theatre
                                    </button>
                                    <button
                                        onClick={() => setCurrentView("all")}
                                        className={`px-3 py-2 rounded-lg border ${currentView === "all"
                                                ? "bg-blue-600 border-blue-500"
                                                : "bg-gray-700 border-gray-600 hover:bg-gray-650"
                                            }`}
                                    >
                                        🧩 All
                                    </button>
                                </div>
                            </div>

                            {/* Miesiąc */}
                            <div className="panel-section">
                                <div className="panel-label">Miesiąc</div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => changeMonth(-1)}
                                        className="h-10 w-10 flex items-center justify-center rounded-lg bg-gray-700 border border-gray-600 hover:bg-gray-650"
                                        aria-label="Poprzedni miesiąc"
                                    >
                                        <MdChevronLeft size={22} />
                                    </button>
                                    <div className="flex-1 text-center px-3 py-2 rounded-lg bg-gray-700 border border-gray-600 font-semibold">
                                        {label}
                                    </div>
                                    <button
                                        onClick={() => changeMonth(1)}
                                        className="h-10 w-10 flex items-center justify-center rounded-lg bg-gray-700 border border-gray-600 hover:bg-gray-650"
                                        aria-label="Następny miesiąc"
                                    >
                                        <MdChevronRight size={22} />
                                    </button>
                                </div>
                            </div>

                            {/* Pracownik + filtr TBD */}
                            <div className="panel-section">
                                <div className="panel-label">Pracownik</div>
                                <EmployeeSearchSelect
                                    employees={employees}
                                    selectedEmployee={selectedEmployee}
                                    setSelectedEmployee={setSelectedEmployee}
                                    tbdOnly={tbdOnly}
                                    setTbdOnly={setTbdOnly}
                                    placeholder="— wszyscy pracownicy —"
                                    panelAlign="right"
                                    showTbdOption={true}
                                />
                            </div>

                            {/* Wydarzenia */}
                            <div className="panel-section">
                                <label className="block text-xs font-semibold mb-2 uppercase tracking-wide opacity-80">
                                    WYDARZENIE
                                </label>
                                
                                {/* Wyszukiwanie tekstowe */}
                                <input
                                    type="text"
                                    value={eventSearchQuery || ""}
                                    onChange={(e) => onChangeEventSearchQuery(e.target.value)}
                                    placeholder="Szukaj wydarzenia..."
                                    className="w-full px-3 py-2 rounded-lg bg-gray-700 border border-gray-600 
                                             text-gray-100 placeholder-gray-400 focus:outline-none 
                                             focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div className="mt-4 flex justify-end">
                                <ThemeToggle />
                            </div>
                        </div>
                    </>
                )}
            </FabPortal>
        </>
    );
}
